/**
 * Common Helper Functions
 * 
 * A collection of utility functions that can be used across the application
 * for common operations like string manipulation, formatting, etc.
 */

/**
 * Capitalizes the first letter of a string and converts the rest to lowercase
 * @param value - The value to capitalize (can be any type)
 * @returns The capitalized string or the original value if not a string
 * 
 * @example
 * capitalizeFirstLetter("hello world") // Returns "Hello world"
 * capitalizeFirstLetter("JOHN DOE") // Returns "John doe"
 * capitalizeFirstLetter(123) // Returns 123 (unchanged)
 * capitalizeFirstLetter("") // Returns "" (unchanged)
 */
export const capitalizeFirstLetter = (value: any): any => {
    if (typeof value === 'string' && value.trim() !== '') {
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }
    return value;
};

/**
 * Capitalizes the first letter of each word in a string
 * @param value - The string to capitalize words in
 * @returns The string with each word capitalized
 * 
 * @example
 * capitalizeWords("hello world") // Returns "Hello World"
 * capitalizeWords("john doe smith") // Returns "John Doe Smith"
 */
export const capitalizeWords = (value: string): string => {
    if (typeof value !== 'string' || value.trim() === '') {
        return value;
    }
    return value.split(' ').map(word => capitalizeFirstLetter(word)).join(' ');
};

/**
 * Formats a string to title case (first letter of each word capitalized)
 * @param value - The string to format
 * @returns The string in title case
 * 
 * @example
 * toTitleCase("hello world") // Returns "Hello World"
 * toTitleCase("john doe smith") // Returns "John Doe Smith"
 */
export const toTitleCase = (value: string): string => {
    return capitalizeWords(value);
};

/**
 * Truncates a string to a specified length and adds ellipsis
 * @param value - The string to truncate
 * @param maxLength - Maximum length before truncation
 * @returns The truncated string with ellipsis if needed
 * 
 * @example
 * truncateString("This is a very long string", 20) // Returns "This is a very long..."
 */
export const truncateString = (value: string, maxLength: number): string => {
    if (typeof value !== 'string' || value.length <= maxLength) {
        return value;
    }
    return value.substring(0, maxLength) + '...';
};

/**
 * Normalizes string spacing by removing extra whitespace
 * @param value - The string to normalize
 * @returns The normalized string with consistent spacing
 * 
 * @example
 * normalizeWhitespace("  hello   world  ") // Returns "hello world"
 */
export const normalizeWhitespace = (value: string): string => {
    if (typeof value !== 'string') {
        return value;
    }
    return value.replace(/\s+/g, ' ').trim();
};


/**
 * Formats a date to "DD MMM YYYY" format
 * @param date - The date to format (can be Date object, string, or timestamp)
 * @returns The formatted date string
 * 
 * @example
 * handleValidDate("2024-01-15T10:30:00Z") // Returns "15 Jan 2024"
 * handleValidDate(new Date()) // Returns current date in format "28 Aug 2025"
 */
export const handleValidDate = (date: any): string => {
    if (!date) return "N/A";
    try {
        const moment = require('moment');
        return moment(new Date(date)).format("DD MMM YYYY");
    } catch (error) {
        // Fallback to basic date formatting if moment is not available
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return "N/A";

        const day = dateObj.getDate().toString().padStart(2, '0');
        const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
        const year = dateObj.getFullYear();

        return `${day} ${month} ${year}`;
    }
};

/**
 * Formats a time to "hh:mm AM/PM" format
 * @param time - The time to format (can be Date object, string, or timestamp)
 * @returns The formatted time string
 * 
 * @example
 * handleValidTime("2024-01-15T10:30:00Z") // Returns "10:30 AM"
 * handleValidTime(new Date()) // Returns current time in format "11:33 AM"
 */
export const handleValidTime = (time: any): string => {
    if (!time) return "N/A";
    try {
        const moment = require('moment');
        const time1 = new Date(time);
        const getHour = time1.getHours();
        const getMin = time1.getMinutes();
        const getTime = `${getHour}:${getMin}`;
        let meridiem = "";
        if (getHour >= 12) {
            meridiem = "PM";
        } else {
            meridiem = "AM";
        }
        return moment(getTime, "hh:mm").format("hh:mm") + " " + meridiem;
    } catch (error) {
        // Fallback to basic time formatting if moment is not available
        const timeObj = new Date(time);
        if (isNaN(timeObj.getTime())) return "N/A";

        const hours = timeObj.getHours();
        const minutes = timeObj.getMinutes().toString().padStart(2, '0');
        const meridiem = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;

        return `${displayHours}:${minutes} ${meridiem}`;
    }
};
